import { Injectable, signal } from '@angular/core';
import type { OmniSearchHistoryEntry, OmniSearchResult } from '@brighthub/search';
import { ClientSearchResultItem } from '../client-search-api.model';

/** `localStorage` key for persisted omni client-search history. Bump only with a migration strategy. */
const STORAGE_KEY = 'bai.omniClientSearchHistory.v1';
/** Serialized envelope version; mismatched payloads are cleared. */
const SCHEMA_VERSION = 1;
/** Hard cap on stored entries after merge, dedupe, and TTL prune. */
const MAX_ENTRIES = 25;
/** Entries older than this are dropped on load and after each update. */
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * On-disk shape for {@link ClientSearchOmniHistoryService}. Kept explicit for validation and forward compatibility.
 */
interface StoredEnvelope {
  version: number;
  entries: OmniSearchHistoryEntry<ClientSearchResultItem>[];
}

/**
 * Persists recent client omni-search runs for the Bright AI UI: in-memory {@link entries} plus `localStorage`
 * hydration and save. Consumed via {@link ClientSearchProvider}'s `searchHistory` attachment.
 *
 * @remarks
 * Uses browser `localStorage` only; not suitable for SSR without guards or a storage abstraction.
 * Corrupt or version-mismatched storage is cleared defensively.
 */
@Injectable({
  providedIn: 'root',
})
export class ClientSearchOmniHistoryService {
  private readonly _entries = signal<OmniSearchHistoryEntry<ClientSearchResultItem>[]>([]);

  /** Read-only list of history entries (newest logical order maintained by {@link recordSearch}). */
  readonly entries = this._entries.asReadonly();

  constructor() {
    this._hydrateFromStorage();
  }

  /**
   * Records a completed search: trims `query`, dedupes by case-insensitive query, prepends the new entry,
   * prunes by TTL and {@link MAX_ENTRIES}, then persists.
   *
   * @param query - Raw query string; empty after trim is a no-op.
   * @param results - Result rows to snapshot into history (cloned shallowly per row `data`).
   */
  recordSearch(
    query: string,
    results: OmniSearchResult<ClientSearchResultItem>[]
  ): void {
    const t = query.trim();
    if (!t) {
      return;
    }
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    const newEntry: OmniSearchHistoryEntry<ClientSearchResultItem> = {
      id,
      query: t,
      results: this._cloneResultsForStorage(results),
      recordedAt: Date.now(),
      resultCount: results.length,
    };
    this._entries.update((list) => {
      const withoutDup = list.filter(
        (e) => e.query.trim().toLowerCase() !== t.toLowerCase()
      );
      const merged = [newEntry, ...withoutDup];
      return this._pruneExpiredList(merged).slice(0, MAX_ENTRIES);
    });
    this._persist();
  }

  /**
   * Removes a history row by id and rewrites storage. Unknown ids are harmless (list unchanged).
   *
   * @param id - Entry id from {@link OmniSearchHistoryEntry}.
   */
  removeEntry(id: string): void {
    this._entries.update((list) => list.filter((e) => e.id !== id));
    this._persist();
  }

  /** Loads {@link STORAGE_KEY} into {@link _entries}; clears key on invalid JSON or schema mismatch. */
  private _hydrateFromStorage(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw) as StoredEnvelope;
      if (
        parsed?.version !== SCHEMA_VERSION ||
        !Array.isArray(parsed['entries'])
      ) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      const entries = (parsed['entries'] as unknown[])
        .map((e) => this._deserializeEntry(e))
        .filter(
          (e): e is OmniSearchHistoryEntry<ClientSearchResultItem> => e !== null
        );
      const pruned = this._pruneExpiredList(entries);
      this._entries.set(pruned);
      if (pruned.length !== entries.length) {
        this._persist();
      }
    } catch {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      this._entries.set([]);
    }
  }

  /**
   * Best-effort parse of one stored entry; returns `null` if required fields are missing or malformed.
   *
   * @param raw - One element from the persisted `entries` array.
   * @returns A typed entry or `null` to drop the row.
   */
  private _deserializeEntry(
    raw: unknown
  ): OmniSearchHistoryEntry<ClientSearchResultItem> | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    const o = raw as Record<string, unknown>;
    const id = typeof o['id'] === 'string' ? o['id'] : null;
    const query = typeof o['query'] === 'string' ? o['query'] : null;
    const recordedAt =
      typeof o['recordedAt'] === 'number' && Number.isFinite(o['recordedAt'])
        ? o['recordedAt']
        : null;
    const resultsRaw = o['results'];
    if (!id || !query || recordedAt === null || !Array.isArray(resultsRaw)) {
      return null;
    }
    const rc = o['resultCount'];
    const resultCount =
      typeof rc === 'number' && Number.isFinite(rc) && rc >= 0
        ? Math.floor(rc)
        : undefined;
    const results: OmniSearchResult<ClientSearchResultItem>[] = [];
    for (const row of resultsRaw) {
      if (!row || typeof row !== 'object') {
        continue;
      }
      const r = row as Record<string, unknown>;
      const title = typeof r['title'] === 'string' ? r['title'] : '';
      const description =
        typeof r['description'] === 'string' ? r['description'] : undefined;
      const data = r['data'];
      if (!data || typeof data !== 'object') {
        continue;
      }
      results.push({
        title,
        description,
        data: data as ClientSearchResultItem,
      });
    }
    const entry: OmniSearchHistoryEntry<ClientSearchResultItem> = {
      id,
      query,
      results,
      recordedAt,
    };
    if (resultCount !== undefined) {
      entry.resultCount = resultCount;
    }
    return entry;
  }

  /** Drops entries whose {@link OmniSearchHistoryEntry.recordedAt} is older than {@link TTL_MS}. */
  private _pruneExpiredList(
    list: OmniSearchHistoryEntry<ClientSearchResultItem>[]
  ): OmniSearchHistoryEntry<ClientSearchResultItem>[] {
    const cutoff = Date.now() - TTL_MS;
    return list.filter((e) => e.recordedAt >= cutoff);
  }

  /** Shallow clones each result and spreads `data` so stored snapshots do not alias live objects. */
  private _cloneResultsForStorage(
    results: OmniSearchResult<ClientSearchResultItem>[]
  ): OmniSearchResult<ClientSearchResultItem>[] {
    return results.map((r) => ({
      title: r.title,
      description: r.description,
      data: { ...r.data },
    }));
  }

  /** Writes the current in-memory list under {@link STORAGE_KEY}; ignores quota / private-mode failures. */
  private _persist(): void {
    try {
      const envelope: StoredEnvelope = {
        version: SCHEMA_VERSION,
        entries: this._entries(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
    } catch {
      /* quota or private mode */
    }
  }
}
